import {
  listenToCollectionViaREST,
  updateDocumentViaREST,
  readDocumentViaREST,
  writeDocumentViaREST,
  deleteDocumentViaREST,
  readCollectionViaREST,
} from './firebaseRestApi';
import { ChatType, ChatTypeWithKey, ConciergeChatType, ConciergeChatTypeWithKey } from '~types/chat';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

class FirebaseChatsServiceWeb {
  // Using REST API instead of SDK - pure HTTP implementation

  subscribeToUserChats(
    userId: string,
    onSuccess: (chats: ChatTypeWithKey[]) => void,
    onError: (error: any) => void
  ) {
    console.log('🔥 [REST-FIRESTORE] Setting up listener for user:', userId);
    console.log('🔥 [REST-FIRESTORE] Query: chats where userIds array-contains', userId);

    // Use REST API listener with array-contains filter
    // Returns unsubscribe function
    const unsubscribe = listenToCollectionViaREST(
      'chats',
      [{ field: 'userIds', operator: 'array-contains', value: userId }],
      (docs) => {
        try {
          console.log('🔥 [REST-FIRESTORE] Chat listener updated:', docs.length, 'documents');
          
          const conversations: ChatTypeWithKey[] = docs.map((doc) => {
            const data = doc as any;
            console.log('✅ [REST-FIRESTORE] Processing chat:', doc.id, {
              users: data.users,
              userIds: data.userIds,
              type: data.type,
              historyLength: data.history?.length || 0,
            });
            return {
              ...data,
              key: doc.id,
            } as ChatTypeWithKey;
          });

          console.log('🔥 [REST-FIRESTORE] Processed conversations:', conversations.length);
          console.log('🔥 [REST-FIRESTORE] Calling onSuccess with', conversations.length, 'conversations');
          onSuccess(conversations);
        } catch (error) {
          console.error('❌ [REST-FIRESTORE] Error processing chat snapshot:', error);
          onError(error);
        }
      },
      (error) => {
        console.error('❌ [REST-FIRESTORE] Chat subscription error:', error);
        onError(error);
      },
      2000 // Poll every 2 seconds
    );

    // Return unsubscribe function
    return unsubscribe;
  }

  subscribeToOneOnOneChat(
    userId: string,
    onSuccess: (chat: ConciergeChatTypeWithKey | null) => void,
    onError: (error: any) => void
  ) {
    console.log('🔥 [REST-FIRESTORE] Setting up listener for one-on-one chat, user:', userId);
    console.log('🔥 [REST-FIRESTORE] Query: conciergeChat where userId == userId AND type == "one-on-one"');

    // Use REST API listener with multiple filters
    const unsubscribe = listenToCollectionViaREST(
      'conciergeChat',
      [
        { field: 'userId', operator: '==', value: userId },
        { field: 'type', operator: '==', value: 'one-on-one' },
      ],
      (docs) => {
        try {
          if (docs.length > 0) {
            const docData = docs[0] as any;
            const chat: ConciergeChatTypeWithKey = {
              ...docData,
              key: docs[0].id,
            };
            console.log('🔥 [REST-FIRESTORE] One-on-one chat found:', chat.key);
            onSuccess(chat);
          } else {
            console.log('🔥 [REST-FIRESTORE] No one-on-one chat found for user');
            onSuccess(null);
          }
        } catch (error) {
          console.error('❌ [REST-FIRESTORE] Error processing one-on-one snapshot:', error);
          onError(error);
        }
      },
      (error) => {
        console.error('❌ [REST-FIRESTORE] One-on-one subscription error:', error);
        onError(error);
      },
      2000 // Poll every 2 seconds
    );

    // Return unsubscribe function
    return unsubscribe;
  }

  subscribeToBroadcastMessages(
    onSuccess: (messages: any[]) => void,
    onError: (error: any) => void
  ) {
    console.log('🔥 [REST-FIRESTORE] Setting up listener for broadcast messages');
    console.log('🔥 [REST-FIRESTORE] Query: conciergeChat where type == "broadcast"');

    const unsubscribe = listenToCollectionViaREST(
      'conciergeChat',
      [{ field: 'type', operator: '==', value: 'broadcast' }],
      (docs) => {
        try {
          const messages: any[] = [];
          docs.forEach((docData: any) => {
            if (docData.messages && Array.isArray(docData.messages)) {
              messages.push(...docData.messages);
            }
          });
          console.log('🔥 [REST-FIRESTORE] Broadcast messages received:', messages.length);
          onSuccess(messages);
        } catch (error) {
          console.error('❌ [REST-FIRESTORE] Error processing broadcast snapshot:', error);
          onError(error);
        }
      },
      (error) => {
        console.error('❌ [REST-FIRESTORE] Broadcast subscription error:', error);
        onError(error);
      },
      2000 // Poll every 2 seconds
    );

    return unsubscribe;
  }

  subscribeToSingleChat(
    chatId: string,
    onSuccess: (chat: ChatTypeWithKey) => void,
    onError: (error: any) => void
  ) {
    console.log('🔥 [REST-FIRESTORE] Setting up listener for single chat:', chatId);

    let lastSnapshot: string = '';
    let isFirstCall = true;

    const pollChat = async () => {
      try {
        const chat = await readDocumentViaREST('chats', chatId);
        if (!chat) {
          throw new Error('Chat not found');
        }

        const chatWithKey: ChatTypeWithKey = {
          ...(chat as ChatType),
          key: chatId,
        };

        const snapshot = JSON.stringify(chatWithKey);

        // Only call callback if data changed or first call
        if (snapshot !== lastSnapshot || isFirstCall) {
          console.log('✅ [REST-FIRESTORE] Chat listener updated:', chatId);
          lastSnapshot = snapshot;
          onSuccess(chatWithKey);
          isFirstCall = false;
        }
      } catch (error) {
        console.error('❌ [REST-FIRESTORE] Error in single chat listener:', error);
        onError(error);
      }
    };

    // Immediate fetch
    pollChat();

    // Set up polling every 2 seconds
    const intervalId = setInterval(pollChat, 2000);

    // Return unsubscribe function
    return () => {
      console.log('👋 [REST-FIRESTORE] Unsubscribing from single chat:', chatId);
      clearInterval(intervalId);
    };
  }

  async createContactUsChat(data: {
    user_id1: string;
    user_id2: string;
    user_name1: string;
    user_name2: string;
  }) {
    try {
      console.log('🔥 [REST-FIRESTORE] Creating contact chat between', data.user_name1, 'and', data.user_name2);

      // Check if chat already exists
      const existingChats = await readCollectionViaREST(
        'chats',
        [{ field: 'userIds', operator: 'array-contains', value: data.user_id1 }]
      );

      let existingChat = false;
      existingChats.forEach((chat: any) => {
        if (
          chat.userIds?.includes(data.user_id1) &&
          chat.userIds?.includes(data.user_id2)
        ) {
          existingChat = true;
        }
      });

      if (!existingChat) {
        await writeDocumentViaREST('chats', {
          userIds: [data.user_id1, data.user_id2],
          users: [data.user_name1, data.user_name2],
          type: 'contact-pyxi',
          history: [],
          createdAt: moment().unix(),
        });
        console.log('✅ [REST-FIRESTORE] Contact chat created');
      } else {
        console.log('ℹ️ [REST-FIRESTORE] Contact chat already exists');
      }
    } catch (error) {
      console.error('❌ [REST-FIRESTORE] Error creating contact chat:', error);
    }
  }

  async sendMessage(
    chatId: string,
    message: {
      text: string;
      sender_id: string;
      sender_name: string;
      timestamp: number;
      sender_image?: string;
    }
  ) {
    try {
      console.log('🔥 [REST-FIRESTORE] Sending message to chat:', chatId);

      // Get existing chat to append message to history
      const chat = await readDocumentViaREST('chats', chatId);
      if (!chat) {
        throw new Error('Chat not found');
      }

      const updatedHistory = Array.isArray(chat.history) ? chat.history : [];
      updatedHistory.push(message);

      // Update chat with new message
      await updateDocumentViaREST('chats', chatId, {
        history: updatedHistory,
        lastMessage: message.text,
        lastMessageTime: moment().unix(),
      });

      console.log('✅ [REST-FIRESTORE] Message sent');
    } catch (error) {
      console.error('❌ [REST-FIRESTORE] Error sending message:', error);
      throw error;
    }
  }

  async pushMessageForUsers({
    chat_id,
    user_id,
    user_image,
    user_name,
    text,
    temp_message_id,
  }: {
    chat_id: string;
    user_id: string;
    user_image: string;
    user_name: string;
    text: string;
    temp_message_id?: string;
  }): Promise<string> {
    try {
      console.log('🔥 [WEB-FIRESTORE] pushMessageForUsers called with:', {
        chat_id,
        user_id,
        user_name,
        text: text.substring(0, 50) + '...',
      });

      // Validate required parameters
      if (!chat_id) {
        throw new Error('chat_id is required');
      }
      if (!user_id) {
        throw new Error('user_id is required');
      }
      if (!text || !text.trim()) {
        throw new Error('text is required and cannot be empty');
      }

      // Get existing chat
      const chatData = await readDocumentViaREST('chats', chat_id);
      if (!chatData) {
        throw new Error(`Chat document with id ${chat_id} does not exist`);
      }

      const chat = chatData as any;

      // Check if user is blocked (but NOT for support chats)
      if (chat?.type !== 'contact-pyxi') {
        const blockedUsers = chat.blockedUsers || {};
        const blockedBy = chat.blockedBy || {};

        // Check if current user has blocked someone in this chat
        const hasBlockedSomeone = Object.values(blockedUsers).some((v: any) => v === true);

        // Check if current user is blocked by someone in this chat
        const isBlockedByOtherUser = Object.keys(blockedBy).includes(user_id);

        if (hasBlockedSomeone) {
          throw new Error('You have blocked this user. Unblock them first to send messages.');
        }

        if (isBlockedByOtherUser) {
          throw new Error('This user has blocked you. Your message cannot be delivered.');
        }
      }

      // Generate message ID
      const message_id = temp_message_id || uuidv4();
      const datetime = moment().toISOString();

      // Create new message following mobile structure
      const newMessage = {
        message_id: message_id,
        message: text,
        text: text,
        sender: user_name,
        sender_name: user_name,
        sender_image: user_image,
        sender_id: user_id,
        timestamp: datetime,
        type: 'message',
        readUserIds: [user_id],
        status: 'sent',
      };

      // Update chat history
      const updatedHistory = Array.isArray(chat.history) ? [...chat.history, newMessage] : [newMessage];

      // Update chat document
      await updateDocumentViaREST('chats', chat_id, {
        history: updatedHistory,
        lastMessage: text,
        lastMessageTime: moment().unix(),
      });

      console.log('✅ [WEB-FIRESTORE] Message sent successfully:', message_id);

      return message_id;
    } catch (error) {
      console.error('❌ [WEB-FIRESTORE] pushMessageForUsers error:', error);
      throw error;
    }
  }

  async getChat(chatId: string): Promise<ChatTypeWithKey | null> {
    try {
      console.log('🔥 [REST-FIRESTORE] Getting chat:', chatId);

      const chat = await readDocumentViaREST('chats', chatId);
      if (!chat) {
        return null;
      }

      return {
        ...(chat as ChatType),
        key: chatId,
      };
    } catch (error) {
      console.error('❌ [REST-FIRESTORE] Error getting chat:', error);
      throw error;
    }
  }
}

export default new FirebaseChatsServiceWeb();
